const SETTINGS_KEY = 'wingit_admin_setup_v1';
const LEGACY_SETTINGS_ORGANISATION_ID =
  '722ff17d-cfe7-49c4-b45f-c42bb3c280ad';

function getSettingsStorageKey(organisationId = '') {
  const normalizedOrganisationId = typeof organisationId === 'string'
    ? organisationId.trim()
    : '';
  return normalizedOrganisationId
    ? `${SETTINGS_KEY}:${normalizedOrganisationId}`
    : SETTINGS_KEY;
}

export const defaultAppSettings = {
  branding: {
    companyLogoDataUrl: '',
  },
  sections: {
    techLogEnabled: true,
    pilotLogbookEnabled: false,
    paymentEnabled: true,
  },
  techLog: {
    recordingMethod: 'hobbs',
    defaultTaxiTimeEnabled: true,
    defaultTaxiTimeMinutes: 12,
    defaultTaxiTimeHours: 0.2,
    hobbsEnabled: true,
    flightTimeEnabled: true,
    taxiTimeEnabled: true,
    snagEnabled: true,
    notesEnabled: true,
  },
  pilotLogbook: {
    allowTaxiInTotals: true,
    notesEnabled: true,
  },
  payment: {
    billingBasis: 'hobbs',
    hourlyRate: 165,
    landingFeeEnabled: false,
    landingFee: 0,
    stripeConnect: {
      mode: 'standard',
      accountId: '',
      onboardingComplete: false,
      chargesEnabled: false,
      payoutsEnabled: false,
      status: '',
      lastCheckedAt: '',
    },
  },
  maintenance: {
    checkIntervalHours: 50,
    warningWindowHours: 5,
  },
  operational: {
    baseAirfield: '',
    medicalWarningDays: 30,
    currencyWarningDays: 7,
    checkFlightWarningDays: 14,
    clubCurrencyWindowDays: 60,
    bookingProhibitOnRed: true,
    nightCurrencyEnabled: true,
    checkFlightEnabled: true,
  },
};

function coerceNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeSettings(raw = {}) {
  const legacyBlockOnRed = raw?.operational?.bookingBlockOnRed;
  const legacyTaxiHours = Math.max(0, coerceNumber(raw?.techLog?.defaultTaxiTimeHours, defaultAppSettings.techLog.defaultTaxiTimeHours));
  const fallbackTaxiMinutes = Math.max(0, Math.round(legacyTaxiHours * 60));
  const normalizedTaxiMinutes = Math.max(0, Math.round(coerceNumber(raw?.techLog?.defaultTaxiTimeMinutes, fallbackTaxiMinutes)));
  const normalizedTaxiHours = Number((normalizedTaxiMinutes / 60).toFixed(2));

  return {
    branding: {
      companyLogoDataUrl: typeof raw?.branding?.companyLogoDataUrl === 'string'
        ? raw.branding.companyLogoDataUrl
        : defaultAppSettings.branding.companyLogoDataUrl,
    },
    sections: {
      techLogEnabled: raw?.sections?.techLogEnabled !== false,
      pilotLogbookEnabled: raw?.sections?.pilotLogbookEnabled === true,
      paymentEnabled: raw?.sections?.paymentEnabled !== false,
    },
    techLog: {
      recordingMethod: ['hobbs', 'datcon', 'tacho'].includes(raw?.techLog?.recordingMethod)
        ? raw.techLog.recordingMethod
        : defaultAppSettings.techLog.recordingMethod,
      defaultTaxiTimeEnabled: raw?.techLog?.defaultTaxiTimeEnabled !== false,
      defaultTaxiTimeMinutes: normalizedTaxiMinutes,
      defaultTaxiTimeHours: normalizedTaxiHours,
      hobbsEnabled: raw?.techLog?.hobbsEnabled !== false,
      flightTimeEnabled: raw?.techLog?.flightTimeEnabled !== false,
      taxiTimeEnabled: raw?.techLog?.taxiTimeEnabled !== false,
      snagEnabled: raw?.techLog?.snagEnabled !== false,
      notesEnabled: raw?.techLog?.notesEnabled !== false,
    },
    pilotLogbook: {
      allowTaxiInTotals: raw?.pilotLogbook?.allowTaxiInTotals !== false,
      notesEnabled: raw?.pilotLogbook?.notesEnabled !== false,
    },
    payment: {
      billingBasis: ['hobbs', 'flight', 'block'].includes(raw?.payment?.billingBasis)
        ? raw.payment.billingBasis
        : defaultAppSettings.payment.billingBasis,
      hourlyRate: coerceNumber(raw?.payment?.hourlyRate, defaultAppSettings.payment.hourlyRate),
      landingFeeEnabled: !!raw?.payment?.landingFeeEnabled,
      landingFee: coerceNumber(raw?.payment?.landingFee, defaultAppSettings.payment.landingFee),
      stripeConnect: {
        mode: String(raw?.payment?.stripeConnect?.mode || defaultAppSettings.payment.stripeConnect.mode).toLowerCase() === 'express'
          ? 'express'
          : 'standard',
        accountId: typeof raw?.payment?.stripeConnect?.accountId === 'string'
          ? raw.payment.stripeConnect.accountId.trim()
          : defaultAppSettings.payment.stripeConnect.accountId,
        onboardingComplete: !!raw?.payment?.stripeConnect?.onboardingComplete,
        chargesEnabled: !!raw?.payment?.stripeConnect?.chargesEnabled,
        payoutsEnabled: !!raw?.payment?.stripeConnect?.payoutsEnabled,
        status: typeof raw?.payment?.stripeConnect?.status === 'string'
          ? raw.payment.stripeConnect.status.trim()
          : defaultAppSettings.payment.stripeConnect.status,
        lastCheckedAt: typeof raw?.payment?.stripeConnect?.lastCheckedAt === 'string'
          ? raw.payment.stripeConnect.lastCheckedAt
          : defaultAppSettings.payment.stripeConnect.lastCheckedAt,
      },
    },
    maintenance: {
      checkIntervalHours: Math.max(1, coerceNumber(raw?.maintenance?.checkIntervalHours, defaultAppSettings.maintenance.checkIntervalHours)),
      warningWindowHours: Math.max(0, coerceNumber(raw?.maintenance?.warningWindowHours, defaultAppSettings.maintenance.warningWindowHours)),
    },
    operational: {
      baseAirfield: typeof raw?.operational?.baseAirfield === 'string'
        ? raw.operational.baseAirfield.trim().toUpperCase()
        : defaultAppSettings.operational.baseAirfield,
      medicalWarningDays: Math.max(1, coerceNumber(raw?.operational?.medicalWarningDays, defaultAppSettings.operational.medicalWarningDays)),
      currencyWarningDays: Math.max(1, coerceNumber(raw?.operational?.currencyWarningDays, defaultAppSettings.operational.currencyWarningDays)),
      checkFlightWarningDays: Math.max(1, coerceNumber(raw?.operational?.checkFlightWarningDays, defaultAppSettings.operational.checkFlightWarningDays)),
      clubCurrencyWindowDays: Math.max(1, coerceNumber(raw?.operational?.clubCurrencyWindowDays, defaultAppSettings.operational.clubCurrencyWindowDays)),
      bookingProhibitOnRed: raw?.operational?.bookingProhibitOnRed !== false && legacyBlockOnRed !== false,
      nightCurrencyEnabled: raw?.operational?.nightCurrencyEnabled !== false,
      checkFlightEnabled: raw?.operational?.checkFlightEnabled !== false,
    },
  };
}

export function loadAppSettings(organisationId = '') {
  try {
    const normalizedOrganisationId = typeof organisationId === 'string'
      ? organisationId.trim()
      : '';

    if (!normalizedOrganisationId) {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (!raw) return defaultAppSettings;
      return normalizeSettings(JSON.parse(raw));
    }

    const settingsStorageKey = getSettingsStorageKey(normalizedOrganisationId);
    const orgRaw = localStorage.getItem(settingsStorageKey);
    if (orgRaw) {
      return normalizeSettings(JSON.parse(orgRaw));
    }

    if (normalizedOrganisationId === LEGACY_SETTINGS_ORGANISATION_ID) {
      const legacyRaw = localStorage.getItem(SETTINGS_KEY);
      if (!legacyRaw) return normalizeSettings(defaultAppSettings);

      const normalizedLegacySettings = normalizeSettings(JSON.parse(legacyRaw));
      localStorage.setItem(settingsStorageKey, JSON.stringify(normalizedLegacySettings));
      return normalizedLegacySettings;
    }

    return normalizeSettings(defaultAppSettings);
  } catch (err) {
    return defaultAppSettings;
  }
}

export function saveAppSettings(settings, organisationId = '') {
  const normalized = normalizeSettings(settings);
  const settingsStorageKey = getSettingsStorageKey(organisationId);
  localStorage.setItem(settingsStorageKey, JSON.stringify(normalized));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('wingit-settings-updated'));
  }
  return normalized;
}